import { QuoteType } from 'src/api-service';

// =============================================================================
// COLUMN WIDTH SCALE
// =============================================================================

/**
 * Column width scale (in rem) - like clothing sizes for easy adjustment.
 * Change these values to adjust all columns using that size.
 */
export enum ColumnWidth {
    XS = 4,    // Icons, checkboxes
    SM = 7.25, // Actions (accounts for child indentation)
    S = 10,    // Short fields
    M = 12,    // Medium fields (littera, dimensions)
    L = 14,    // Numbers, percentages, prices
    XL = 16,   // Text fields (name, descriptions)
    XXL = 22,   // Text fields (name, descriptions)
}

// =============================================================================
// COLUMN DEFINITION
// =============================================================================

export interface ColumnDefinition {
    field: string;
    header: string;
    tooltip?: string;         // Översättningsnyckel för tooltip (info-ikon)
    subHeader?: string;       // Översättningsnyckel för undertext (t.ex. "max 50 tecken")
    icon?: string;            // PrimeNG ikon-klass (t.ex. 'pi-sitemap')
    visible: boolean;
    frozen: boolean;
    width: ColumnWidth;
    category: 'none' | 'core' | 'text' | 'vendor' | 'customer';

    // Field behavior (for items only, including sub-items)
    /**
     * Readonly state:
     * - 'always' = always readonly
     * - 'catalogRow' = copied 1:1 from the product catalog; locked on catalog rows (and their children) for every role
     */
    readonly?: 'always' | 'catalogRow';
    /** Required field type for validation (shows warning icons when missing) */
    required?: 'text' | 'object' | 'numeric';
    /** Only show this column for a specific quote type */
    quoteType?: QuoteType;
}

// Prefix för alla översättningsnycklar
const I18N_PREFIX = 'QUOTE.DETAIL.ITEMS.';

// =============================================================================
// COLUMN DEFINITIONS
// =============================================================================

export const COLUMN_DEFINITIONS: ColumnDefinition[] = [
    // Core columns
    { field: 'actions', header: I18N_PREFIX + 'TBL_ACTIONS', visible: true, frozen: true, width: ColumnWidth.SM, category: 'core' },
    { field: 'sortOrder', header: '#', visible: true, frozen: false, width: ColumnWidth.XS, category: 'none' },
    { field: 'image', header: I18N_PREFIX + 'TBL_IMAGE', icon: 'pi-image', visible: false, frozen: false, width: ColumnWidth.XS, category: 'core' },
    { field: 'id', header: I18N_PREFIX + 'TBL_ID', visible: false, frozen: false, width: ColumnWidth.S, category: 'none' },
    { field: 'agreementType', header: I18N_PREFIX + 'TBL_AGREEMENT_TYPE', visible: false, frozen: false, width: ColumnWidth.S, category: 'text' },

    // Text columns
    { field: 'name', header: I18N_PREFIX + 'TBL_NAME', tooltip: I18N_PREFIX + 'TTP_NAME', visible: true, frozen: true, width: ColumnWidth.XL, category: 'text', required: 'text' },
    { field: 'brand', header: I18N_PREFIX + 'TBL_BRAND', tooltip: I18N_PREFIX + 'TTP_BRAND', visible: true, frozen: false, width: ColumnWidth.XL, category: 'text', required: 'object' },
    { field: 'descriptionInternal', header: I18N_PREFIX + 'TBL_DESCRIPTION_INTERNAL', tooltip: I18N_PREFIX + 'TTP_DESCRIPTION_INTERNAL', visible: true, frozen: false, width: ColumnWidth.XL, category: 'text' },
    { field: 'descriptionCustomer', header: I18N_PREFIX + 'TBL_DESCRIPTION_CUSTOMER', tooltip: I18N_PREFIX + 'TTP_DESCRIPTION_CUSTOMER', subHeader: I18N_PREFIX + 'SUB_TBL_DESCRIPTION_CUSTOMER', visible: true, frozen: false, width: ColumnWidth.XL, category: 'text' },
    { field: 'vendorItemNo', header: I18N_PREFIX + 'TBL_VENDOR_ITEM_NO', tooltip: I18N_PREFIX + 'TTP_VENDOR_ITEM_NO', visible: true, frozen: false, width: ColumnWidth.XL, category: 'text' },
    { field: 'typeCaseNumber', header: I18N_PREFIX + 'TBL_TYPE_CASE_NUMBER', tooltip: I18N_PREFIX + 'TTP_TYPE_CASE_NUMBER', visible: false, frozen: false, width: ColumnWidth.M, category: 'text' },
    { field: 'ecoLabels', header: I18N_PREFIX + 'TBL_ECO_LABELS', tooltip: I18N_PREFIX + 'TTP_ECO_LABELS', visible: false, frozen: false, width: ColumnWidth.L, category: 'text' },
    { field: 'dimensions', header: I18N_PREFIX + 'TBL_DIMENSIONS', tooltip: I18N_PREFIX + 'TTP_DIMENSIONS', visible: true, frozen: false, width: ColumnWidth.M, category: 'text' },
    { field: 'littera', header: I18N_PREFIX + 'TBL_LITTERA', tooltip: I18N_PREFIX + 'TTP_LITTERA', visible: true, frozen: false, width: ColumnWidth.M, category: 'text' },
    { field: 'itemCategory', header: I18N_PREFIX + 'TBL_ITEM_CATEGORY', tooltip: I18N_PREFIX + 'TTP_CATEGORY', visible: true, frozen: false, width: ColumnWidth.XL, category: 'text', required: 'object' },

    // Vendor columns
    { field: 'quantity', header: I18N_PREFIX + 'TBL_QUANTITY', tooltip: I18N_PREFIX + 'TTP_QUANTITY', visible: true, frozen: false, width: ColumnWidth.S, category: 'core' },
    { field: 'vendorPrice', header: I18N_PREFIX + 'TBL_VENDOR_PRICE', tooltip: I18N_PREFIX + 'TTP_VENDOR_PRICE', subHeader: I18N_PREFIX + 'SUB_VENDOR_PRICE', visible: true, frozen: false, width: ColumnWidth.L, category: 'vendor' },
    { field: 'vendorDiscountPercent', header: I18N_PREFIX + 'TBL_VENDOR_DISCOUNT_PERCENT', tooltip: I18N_PREFIX + 'TTP_VENDOR_DISCOUNT_PERCENT', subHeader: I18N_PREFIX + 'SUB_VENDOR_DISCOUNT_PERCENT', visible: true, frozen: false, width: ColumnWidth.L, category: 'vendor' },
    { field: 'vendorExtraDiscountPercent', header: I18N_PREFIX + 'TBL_VENDOR_EXTRA_DISCOUNT_PERCENT', tooltip: I18N_PREFIX + 'TTP_VENDOR_EXTRA_DISCOUNT_PERCENT', subHeader: I18N_PREFIX + 'SUB_VENDOR_EXTRA_DISCOUNT_PERCENT', visible: true, frozen: false, width: ColumnWidth.L, category: 'vendor' },
    { field: 'unitCost', header: I18N_PREFIX + 'TBL_UNIT_COST', tooltip: I18N_PREFIX + 'TTP_UNIT_COST', subHeader: I18N_PREFIX + 'SUB_UNIT_COST', visible: true, frozen: false, width: ColumnWidth.L, category: 'vendor', readonly: 'always' },

    // Customer columns
    { field: 'salesDiscountPercent', header: I18N_PREFIX + 'TBL_SALES_DISCOUNT_PERCENT', tooltip: I18N_PREFIX + 'TTP_SALES_DISCOUNT_PERCENT', subHeader: I18N_PREFIX + 'SUB_SALES_DISCOUNT_PERCENT', visible: true, frozen: false, width: ColumnWidth.L, category: 'customer' },
    { field: 'markupPercent', header: I18N_PREFIX + 'TBL_MARKUP_PERCENT', tooltip: I18N_PREFIX + 'TTP_MARKUP_PERCENT', subHeader: I18N_PREFIX + 'SUB_MARKUP_PERCENT', visible: true, frozen: false, width: ColumnWidth.L, category: 'customer' },
    { field: 'salesUnitPriceExVat', header: I18N_PREFIX + 'TBL_SALES_UNIT_PRICE', tooltip: I18N_PREFIX + 'TTP_SALES_UNIT_PRICE', subHeader: I18N_PREFIX + 'SUB_SALES_UNIT_PRICE', visible: true, frozen: false, width: ColumnWidth.L, category: 'customer' },
    { field: 'isOneTimeCost', header: I18N_PREFIX + 'TBL_IS_ONE_TIME_COST', tooltip: I18N_PREFIX + 'TTP_IS_ONE_TIME_COST', visible: true, frozen: false, width: ColumnWidth.S, category: 'customer', quoteType: QuoteType.Subscription },
    { field: 'grossProfit1', header: I18N_PREFIX + 'TBL_GROSS_PROFIT', tooltip: I18N_PREFIX + 'TTP_GROSS_PROFIT', subHeader: I18N_PREFIX + 'SUB_GROSS_PROFIT', visible: true, frozen: false, width: ColumnWidth.L, category: 'customer', readonly: 'always' },
    { field: 'grossMargin1', header: I18N_PREFIX + 'TBL_GROSS_MARGIN', tooltip: I18N_PREFIX + 'TTP_GROSS_MARGIN', subHeader: I18N_PREFIX + 'SUB_GROSS_MARGIN', visible: true, frozen: false, width: ColumnWidth.L, category: 'customer' },
    { field: 'totalSalesPriceExVat', header: I18N_PREFIX + 'TBL_TOTAL_SALES_PRICE', tooltip: I18N_PREFIX + 'TTP_TOTAL_SALES_PRICE', subHeader: I18N_PREFIX + 'SUB_TOTAL_SALES_PRICE', visible: true, frozen: false, width: ColumnWidth.L, category: 'customer', readonly: 'always' },
];

// Genererade hjälpkonstanter från COLUMN_DEFINITIONS
export const DEFAULT_COLUMN_WIDTHS: Record<string, number> = COLUMN_DEFINITIONS.reduce(
    (acc, col) => ({ ...acc, [col.field]: col.width }),
    {} as Record<string, number>
);

export const DEFAULT_COLUMN_ORDER: string[] = COLUMN_DEFINITIONS.map(col => col.field);

export const DEFAULT_FROZEN_COLUMNS: Record<string, boolean> = COLUMN_DEFINITIONS.reduce(
    (acc, col) => ({ ...acc, [col.field]: col.frozen }),
    {} as Record<string, boolean>
);

export const DEFAULT_VISIBLE_COLUMNS: string[] = COLUMN_DEFINITIONS
    .filter(col => col.visible)
    .map(col => col.field);
